import React, { FC, useCallback, useEffect, useState } from "react";

import { faArrowsRepeat } from "@fortawesome/pro-solid-svg-icons/faArrowsRepeat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import uniq from "lodash-es/uniq";
import { useIntl } from "react-intl";

import { RequiredFields } from "@mapmycustomers/shared";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import PinLegendClassificationType from "@mapmycustomers/shared/enum/pinLegends/PinLegendClassificationType";
import PinLegendDatingMethod from "@mapmycustomers/shared/enum/pinLegends/PinLegendDatingMethod";
import { Breakpoints } from "@mapmycustomers/shared/types/map/PinLegend";
import { Button, NumberField } from "@mapmycustomers/ui";

import { DEFAULT_BUCKETS_COUNT } from "@app/scene/map/components/pinLegends/utils/consts";
import { formatDate } from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";

import styles from "./ClassificationEditor.module.scss";
import BreakpointsField from "./components/BreakpointsField";
import ColorThemeField from "./components/ColorThemeField";
import DataLevelField from "./components/DataLevelField";
import DatingMethodField from "./components/DatingMethodField";
import {
  ClassificationMethodInfo,
  DataLevelInfo,
  DatingMethodInfo,
} from "./components/infoTooltips";
import MethodField from "./components/MethodField";
import StyleSection from "./components/StyleSection";
import messages from "./messages";
import ClassificationStyleEditorProps from "./types/ClassificationStyleEditorProps";

const MIN_BUCKETS = 3;

export interface ColorsClassificationEditorProps
  extends RequiredFields<ClassificationStyleEditorProps, "colors" | "defaultColor"> {}

const supportedClassifications = [
  PinLegendClassificationType.MANUAL,
  PinLegendClassificationType.EQUAL_INTERVAL,
  PinLegendClassificationType.QUANTILE,
  PinLegendClassificationType.NATURAL_BREAKS,
];

const ColorsClassificationEditor: FC<ColorsClassificationEditorProps> = ({
  breakpoints,
  classification,
  colors,
  dataLevel,
  datingMethod,
  defaultColor,
  field,
  onBreakpointsChange,
  onClassificationChange,
  onColorChange,
  onDataLevelChange,
  onDatingMethodChange,
  onDefaultColorChange,
  onError,
}) => {
  const intl = useIntl();

  const [buckets, setBuckets] = useState<number | undefined>(
    colors?.length || DEFAULT_BUCKETS_COUNT
  );
  useEffect(() => {
    setBuckets(colors?.length || DEFAULT_BUCKETS_COUNT);
  }, [colors?.length]);

  const handleBreakpointsChange = useCallback(
    (breakpoints: Breakpoints) => {
      onBreakpointsChange?.(breakpoints);

      const numberOfBuckets = breakpoints.length ? breakpoints.length + 1 : MIN_BUCKETS;
      setBuckets(numberOfBuckets);
    },
    [onBreakpointsChange]
  );

  const handleClassificationChange = useCallback(
    (classification: PinLegendClassificationType) => {
      onClassificationChange?.(classification);
      if (classification === PinLegendClassificationType.MANUAL && breakpoints?.length) {
        setBuckets(breakpoints.length + 1);
      } else {
        setBuckets(MIN_BUCKETS);
      }
    },
    [breakpoints?.length, onClassificationChange]
  );

  const [colorsFlipped, setColorsFlipped] = useState(false);

  const handleFlipClick = useCallback(() => {
    onColorChange?.(colors.toReversed());
    setColorsFlipped((flipped) => !flipped);
  }, [colors, onColorChange]);

  const handleDatingMethodChange = useCallback(
    (value: PinLegendDatingMethod) => {
      // if switching from absolute to ordinal, let's preserve all values
      // in new format removing duplicates
      // otherwise, remove all breakpoints
      if (
        datingMethod === PinLegendDatingMethod.ABSOLUTE &&
        value === PinLegendDatingMethod.ORDINAL
      ) {
        const updatedBreakpoints = uniq(
          breakpoints
            ?.map((date) => parseApiDate(date))
            .filter(isValidDate)
            .map((date) => formatDate(date, "MM-dd"))
        );
        handleBreakpointsChange(updatedBreakpoints);
      } else {
        handleBreakpointsChange([]);
      }

      onDatingMethodChange?.(value);
    },
    [breakpoints, datingMethod, handleBreakpointsChange, onDatingMethodChange]
  );

  const isDateField = field.type === FieldType.DATE || field.type === FieldType.DATE_TIME;
  let stepIndex = 1;

  return (
    <div className={styles.container}>
      <StyleSection
        info={<DataLevelInfo />}
        title={intl.formatMessage(messages.accessLevelStep, { index: stepIndex++ })}
      >
        <DataLevelField onChange={onDataLevelChange} value={dataLevel} />
      </StyleSection>
      {isDateField && (
        <StyleSection
          info={<DatingMethodInfo />}
          title={intl.formatMessage(messages.datingMethodStep, { index: stepIndex++ })}
        >
          <DatingMethodField onChange={handleDatingMethodChange} value={datingMethod} />
        </StyleSection>
      )}
      <StyleSection
        info={<ClassificationMethodInfo />}
        title={intl.formatMessage(messages.classificationStep, { index: stepIndex++ })}
      >
        <MethodField
          classifications={supportedClassifications}
          onChange={handleClassificationChange}
          value={classification}
        />
      </StyleSection>
      <StyleSection title={intl.formatMessage(messages.bucketsStep, { index: stepIndex++ })}>
        {classification === PinLegendClassificationType.MANUAL ? (
          <BreakpointsField
            breakpoints={breakpoints}
            datingMethod={datingMethod}
            field={field}
            onChange={handleBreakpointsChange}
            onError={onError}
          />
        ) : (
          <NumberField max={9} min={MIN_BUCKETS} onChange={setBuckets} value={buckets} />
        )}
      </StyleSection>
      <StyleSection
        action={
          <Button className={styles.flipButton} onClick={handleFlipClick} type="link">
            {intl.formatMessage(messages.flip, {
              icon: <FontAwesomeIcon icon={faArrowsRepeat} />,
            })}
          </Button>
        }
        title={intl.formatMessage(messages.colorThemeStep, { index: stepIndex++ })}
      >
        <ColorThemeField
          colors={colors}
          defaultColor={defaultColor}
          flip={colorsFlipped}
          numberOfBuckets={buckets ?? 0}
          onChange={onColorChange}
          onDefaultColorChange={onDefaultColorChange}
          onFlipChange={setColorsFlipped}
        />
      </StyleSection>
    </div>
  );
};

export default ColorsClassificationEditor;
